import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Crown, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const UPCOMING = [
  { title: "Finca Los Perales", desc: "Recorrido en vídeo por Los Perales: llanos, encinares, zonas de agua y cascadas de la Ruta del Agua." },
  { title: "El Embarcadero", desc: "Vídeo de la finca El Embarcadero y sus mejores puestos de la temporada." },
  { title: "Masterclass: tiro y aproximación", desc: "Técnicas de aproximación y tiro en rececho con nuestros guías." },
];

export default function VideoClub() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", { ...form, type: "videoclub" });
      toast.success(data.message);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24" data-testid="videoclub-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="overline-gold mb-4 flex items-center gap-2"><Crown size={14} /> Contenido del club</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight">
          Video <span className="text-gold italic">Club</span>
        </h1>
        <p className="mt-5 text-white/60 font-light max-w-2xl">
          Documentales, crónicas de caza y masterclasses grabadas en nuestras fincas. El Video Club completo es
          exclusivo para socios: estrenos cada mes y acceso a todo el archivo.
        </p>
      </motion.div>

      {/* VÍDEO DESTACADO */}
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="mt-14" data-testid="featured-video">
        <p className="overline-gold mb-3">Último vídeo del club</p>
        <h2 className="text-2xl lg:text-3xl text-white font-light mb-6">La berrea en <span className="text-gold italic">Sierra Morena</span></h2>
        <div className="relative w-full aspect-video border border-white/10 overflow-hidden bg-black">
          <iframe
            src="https://www.youtube.com/embed/fsefmOqlx1k"
            title="La berrea en Sierra Morena — Cinegética Ruta del Agua"
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="youtube-berrea"
          />
        </div>
      </motion.div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
        {UPCOMING.map((v, i) => (
          <motion.div key={v.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
            className="card-dark p-7" data-testid={`video-card-${i}`}>
            <div className="w-12 h-12 rounded-full border border-gold/40 flex items-center justify-center mb-5">
              <Play size={18} strokeWidth={1.5} className="text-gold ml-0.5" />
            </div>
            <h3 className="text-xl text-white font-medium">{v.title}</h3>
            <p className="mt-3 text-white/55 text-sm font-light leading-relaxed">{v.desc}</p>
            <p className="mt-5 text-[10px] uppercase tracking-[0.2em] text-gold">Próximamente · Solo socios</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <div>
          <h2 className="text-3xl text-white font-light">¿Quieres acceder a todo el contenido?</h2>
          <p className="mt-4 text-white/60 text-sm font-light leading-relaxed">
            Hazte socio por 100 €/año y desbloquea el Video Club completo, los sorteos exclusivos y las ofertas del club.
          </p>
          {user ? (
            <p className="mt-6 text-sand text-sm" data-testid="videoclub-member-note">
              Ya tienes cuenta. {user.is_member ? "Tu membresía está activa: el Video Club completo llegará a tu área de socio muy pronto." : "Activa tu membresía para desbloquear todo el contenido."}
            </p>
          ) : (
            <p className="mt-6 text-sand text-sm">¿Ya eres socio? <Link to="/login" className="text-gold underline underline-offset-4">Inicia sesión</Link></p>
          )}
          <Link to="/hazte-socio" className="btn-gold-solid mt-8" data-testid="videoclub-cta">Hazte Socio</Link>
        </div>
        <form onSubmit={submit} className="card-dark p-7 space-y-4" data-testid="videoclub-form">
          <p className="text-white text-lg font-medium">Sugerir contenido / consulta</p>
          <input className="input-dark" placeholder="Nombre" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="videoclub-name" />
          <input className="input-dark" type="email" placeholder="Email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="videoclub-email" />
          <textarea className="input-dark min-h-[100px]" placeholder="¿Qué te gustaría ver en el Video Club?" required value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="videoclub-message" />
          <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-40" data-testid="videoclub-submit">
            <Send size={15} /> {busy ? "Enviando..." : "Enviar"}
          </button>
        </form>
      </div>
    </div>
  );
}
